// Client for GET /api/sentiment/top-movers (NASDAQ momentum scanner).
// Network errors are reported via lib/logger.ts (Sentry + RTDB appErrors),
// never console.error directly — see CLAUDE.md Rule #1 test/error conventions.
import { logError } from '../lib/logger';

export interface SentimentMover {
  symbol: string;
  exchange: string;
  momentumScore: number;
  mentionCount: number;
}

interface SentimentMoverResponse {
  symbol: string;
  exchange: string;
  momentum_score: number;
  mention_count: number;
}

export async function fetchTopMovers(serverUrl: string): Promise<SentimentMover[]> {
  try {
    const res = await fetch(`${serverUrl}/api/sentiment/top-movers`);
    if (!res.ok) throw new Error(`Sentiment top-movers endpoint returned HTTP ${res.status}`);
    const data: SentimentMoverResponse[] = await res.json();
    return data.map((m) => ({
      symbol: m.symbol,
      exchange: m.exchange,
      momentumScore: m.momentum_score,
      mentionCount: m.mention_count,
    }));
  } catch (err) {
    void logError(err, 'sentimentMoversService.fetchTopMovers');
    throw err;
  }
}

export interface MomentumSourcesConfig {
  redditClientId: string;
  redditClientSecret: string;
  twitterBearerToken: string;
  finnhubApiKey: string;
}

interface MomentumSourcesPayload {
  reddit_client_id: string;
  reddit_client_secret: string;
  twitter_bearer_token: string;
  finnhub_api_key: string;
}

export async function saveMomentumSourcesConfig(
  serverUrl: string,
  config: MomentumSourcesConfig,
): Promise<void> {
  try {
    const payload: MomentumSourcesPayload = {
      reddit_client_id: config.redditClientId,
      reddit_client_secret: config.redditClientSecret,
      twitter_bearer_token: config.twitterBearerToken,
      finnhub_api_key: config.finnhubApiKey,
    };
    const res = await fetch(`${serverUrl}/api/sentiment/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Sentiment config endpoint returned HTTP ${res.status}`);
  } catch (err) {
    void logError(err, 'sentimentMoversService.saveMomentumSourcesConfig');
    throw err;
  }
}

export interface MomentumDiagnostics {
  marketOpen: boolean;
  redditConfigured: boolean;
  twitterConfigured: boolean;
  finnhubConfigured: boolean;
  lastScanAt: number | null;
  tradingviewOk: boolean;
  candidateCount: number;
  redditMentionsTotal: number;
  twitterMentionsTotal: number;
  newsMentionsTotal: number;
  topMoversCount: number;
}

interface MomentumDiagnosticsResponse {
  market_open: boolean;
  reddit_configured: boolean;
  twitter_configured: boolean;
  finnhub_configured: boolean;
  last_scan_at: number | null;
  tradingview_ok: boolean;
  candidate_count: number;
  reddit_mentions_total: number;
  twitter_mentions_total: number;
  news_mentions_total: number;
  top_movers_count: number;
}

export async function fetchMomentumDiagnostics(serverUrl: string): Promise<MomentumDiagnostics> {
  try {
    const res = await fetch(`${serverUrl}/api/sentiment/diagnostics`);
    if (!res.ok) throw new Error(`Sentiment diagnostics endpoint returned HTTP ${res.status}`);
    const d: MomentumDiagnosticsResponse = await res.json();
    return {
      marketOpen: d.market_open,
      redditConfigured: d.reddit_configured,
      twitterConfigured: d.twitter_configured,
      finnhubConfigured: d.finnhub_configured,
      lastScanAt: d.last_scan_at,
      tradingviewOk: d.tradingview_ok,
      candidateCount: d.candidate_count,
      redditMentionsTotal: d.reddit_mentions_total,
      twitterMentionsTotal: d.twitter_mentions_total,
      newsMentionsTotal: d.news_mentions_total,
      topMoversCount: d.top_movers_count,
    };
  } catch (err) {
    void logError(err, 'sentimentMoversService.fetchMomentumDiagnostics');
    throw err;
  }
}

export type MomentumLogLevel = 'info' | 'warning' | 'error';

export interface MomentumLogEntry {
  timestamp: number;
  level: MomentumLogLevel;
  message: string;
}

interface MomentumLogEntryResponse {
  timestamp: number;
  level: string;
  message: string;
}

export async function fetchMomentumLogs(
  serverUrl: string,
  limit = 100,
): Promise<MomentumLogEntry[]> {
  try {
    const res = await fetch(`${serverUrl}/api/sentiment/logs?limit=${limit}`);
    if (!res.ok) throw new Error(`Sentiment logs endpoint returned HTTP ${res.status}`);
    const data: MomentumLogEntryResponse[] = await res.json();
    return data.map((e) => ({
      timestamp: e.timestamp,
      level: (e.level === 'warning' || e.level === 'error' ? e.level : 'info') as MomentumLogLevel,
      message: e.message,
    }));
  } catch (err) {
    void logError(err, 'sentimentMoversService.fetchMomentumLogs');
    throw err;
  }
}

export async function clearMomentumLogs(serverUrl: string): Promise<void> {
  try {
    const res = await fetch(`${serverUrl}/api/sentiment/logs`, { method: 'DELETE' });
    if (!res.ok) throw new Error(`Sentiment logs endpoint returned HTTP ${res.status}`);
  } catch (err) {
    void logError(err, 'sentimentMoversService.clearMomentumLogs');
    throw err;
  }
}

export async function triggerMomentumScan(serverUrl: string): Promise<void> {
  try {
    const res = await fetch(`${serverUrl}/api/sentiment/scan`, { method: 'POST' });
    if (!res.ok) throw new Error(`Sentiment scan endpoint returned HTTP ${res.status}`);
  } catch (err) {
    void logError(err, 'sentimentMoversService.triggerMomentumScan');
    throw err;
  }
}

export interface EngineControlStatus {
  botActive: boolean;
  strategyMode: string;
}

// Reuses the same GET /api/bot/status + /api/config/status endpoints the
// Dashboard/Settings tabs poll, so the Momentum tab's "is the engine even
// running, and in sentiment_momentum mode?" lights reflect the same state.
export async function fetchEngineControlStatus(serverUrl: string): Promise<EngineControlStatus> {
  try {
    const [botRes, configRes] = await Promise.all([
      fetch(`${serverUrl}/api/bot/status`),
      fetch(`${serverUrl}/api/config/status`),
    ]);
    if (!botRes.ok) throw new Error(`Bot status endpoint returned HTTP ${botRes.status}`);
    if (!configRes.ok) throw new Error(`Config status endpoint returned HTTP ${configRes.status}`);
    const botData: { active: boolean } = await botRes.json();
    const configData: { strategy_mode: string } = await configRes.json();
    return { botActive: botData.active, strategyMode: configData.strategy_mode };
  } catch (err) {
    void logError(err, 'sentimentMoversService.fetchEngineControlStatus');
    throw err;
  }
}
